import { InvoiceEntry } from '@dataTypes/billing.types'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_BILLING', '')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type CreateInvoiceEntryRequest = {
  appointmentIds: string[]
  discountPercentage?: number
}

export type GetInvoiceEntriesResponse = ApiResponse<{
  entries?: InvoiceEntry[]
  count?: number
}>

export type CreateInvoiceEntryResponse = ApiResponse<{
  invoice?: unknown
}>

export type DeleteInvoiceEntryResponse = ApiResponse<Record<string, never>>

export type InvoiceQueryFilters = {
  patient_id?: string
  clinic_id?: string
  invoice_status?: string | string[]
  search?: string
  limit?: number
  offset?: number
  sort?: string
}

export const getInvoiceEntries = async (
  filters?: InvoiceQueryFilters,
): Promise<GetInvoiceEntriesResponse> => {
  try {
    const params: Record<string, string | number | string[] | undefined> = {}
    if (filters?.patient_id) params.patient_id = filters.patient_id
    if (filters?.clinic_id) params.clinic_id = filters.clinic_id
    if (filters?.invoice_status !== undefined) {
      params.invoice_status = Array.isArray(filters.invoice_status)
        ? filters.invoice_status
        : filters.invoice_status
    }
    if (filters?.search) params.search = filters.search
    if (filters?.limit !== undefined) params.limit = filters.limit
    if (filters?.offset !== undefined) params.offset = filters.offset
    if (filters?.sort) params.sort = filters.sort
    const response = await axios.get<GetInvoiceEntriesResponse>(
      `${BUSINESS_LAYER_API}/api/v1/invoice`,
      {
        params,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getInvoiceEntries:', e)
    throw e
  }
}

export const createInvoiceEntry = async (
  payload: CreateInvoiceEntryRequest,
): Promise<CreateInvoiceEntryResponse> => {
  try {
    const response = await axios.post<CreateInvoiceEntryResponse>(
      `${BUSINESS_LAYER_API}/api/v1/invoice`,
      payload,
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createInvoiceEntry:', e)
    throw e
  }
}

export const deleteInvoiceEntry = async (
  id: string,
): Promise<DeleteInvoiceEntryResponse> => {
  try {
    const response = await axios.delete<DeleteInvoiceEntryResponse>(
      `${BUSINESS_LAYER_API}/api/v1/invoice/${id}`,
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteInvoiceEntry:', e)
    throw e
  }
}

export type ExportInvoiceCsvFilters = {
  patient_id?: string
  clinic_id?: string | string[]
  invoice_status?: string | string[]
  search?: string
  sort?: string
}

export const exportInvoiceEntriesCsv = async (
  filters?: ExportInvoiceCsvFilters,
): Promise<Blob> => {
  const params: Record<string, string | string[]> = {}
  if (filters?.patient_id) params.patient_id = filters.patient_id
  if (filters?.clinic_id) {
    params.clinic_id = Array.isArray(filters.clinic_id)
      ? filters.clinic_id
      : filters.clinic_id
  }
  if (filters?.search) params.search = filters.search
  if (filters?.sort) params.sort = filters.sort
  if (filters?.invoice_status !== undefined) {
    params.invoice_status = Array.isArray(filters.invoice_status)
      ? filters.invoice_status
      : filters.invoice_status
  }
  const response = await axios.get<Blob>(
    `${BUSINESS_LAYER_API}/api/v1/invoice/export/csv`,
    { params, responseType: 'blob' },
  )
  return response.data
}
